import React, { useEffect, useState } from "react";
import { Edit, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function MeasurementTable() {
  const [measurementsData, setMeasurementsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const fetchData = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://naturalx.in/naturx_api/api/measurements?page=${page}${
          search ? `&search=${search}` : ""
        }`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ company_id: 3 }),
        }
      );
      const data = await response.json();
      setMeasurementsData(data);
      setCurrentPage(data.current_page);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchData(1, searchTerm);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= measurementsData?.last_page) {
      fetchData(page, searchTerm);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleEdit = (measurement) => {
    navigate(`/measurement/${measurement.id}`);
    // Add your edit logic here
  };

  const renderPagination = () => {
    if (!measurementsData || measurementsData.last_page <= 1) return null;

    const totalPages = measurementsData.last_page;
    const current = currentPage;
    const pages = [];

    // Always show first page
    pages.push(1);

    // Calculate range around current page
    let start = Math.max(2, current - 1);
    let end = Math.min(totalPages - 1, current + 1);

    // Add ellipsis after first page if needed
    if (start > 2) {
      pages.push("...");
    }

    // Add pages around current
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (end < totalPages - 1) {
      pages.push("...");
    }

    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return (
      <div className="flex mt-12 flex-col sm:flex-row items-center justify-between gap-4 mt-6">
        <div className="text-sm text-gray-600">
          Showing {measurementsData.from} to {measurementsData.to} of{" "}
          {measurementsData.total} entries
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-center">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
          >
            <ChevronLeft size={16} />
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="flex items-center gap-2">
            {pages.map((page, index) =>
              page === "..." ? (
                <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 rounded border text-sm min-w-[40px] ${
                    currentPage === page
                      ? "bg-[#4B35CB] text-white border-[#4B35CB]"
                      : "border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {page}
                </button>
              )
            )}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === measurementsData?.last_page}
            className="px-3 py-2 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full  mt-12 min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
              Measurements Table
            </h2>
       <button
              type="button"
              onClick={() => navigate("/measurement")}
              className="px-6 mt-3 mb-2 py-2 border border-gray-500 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
              title="Print all data in A4 format"
            >
              + Add New Measurement
            </button>
            {/* Search Bar */}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by client, architect, manager name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B35CB] focus:border-transparent"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4B35CB] mx-auto mb-4"></div>
                <p className="text-lg text-gray-600">
                  Loading measurements data...
                </p>
              </div>
            </div>
          ) : measurementsData?.data && measurementsData.data.length > 0 ? (
            <div>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 border-b text-left font-semibold text-gray-700 text-sm">
                        S.No
                      </th>
                      <th className="py-3 px-4 border-b text-left font-semibold text-gray-700 text-sm">
                        Client Name
                      </th>
                      <th className="py-3 px-4 border-b text-left font-semibold text-gray-700 text-sm">
                        Architect Name
                      </th>
                      <th className="py-3 px-4 border-b text-left font-semibold text-gray-700 text-sm">
                        Manager Name
                      </th>
                      <th className="py-3 px-4 border-b text-left font-semibold text-gray-700 text-sm">
                        Mobile
                      </th>
                      <th className="py-3 px-4 border-b text-left font-semibold text-gray-700 text-sm">
                        Google Address
                      </th>
                      <th className="py-3 px-4 border-b text-left font-semibold text-gray-700 text-sm">
                        Google Chat
                      </th>
                      <th className="py-3 px-4 border-b text-center font-semibold text-gray-700 text-sm">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {measurementsData.data.map((measurement, index) => (
                      <tr
                        key={measurement.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-3 px-4 border-b text-gray-700 text-sm">
                          {(currentPage - 1) * measurementsData.per_page +
                            index +
                            1}
                        </td>
                        <td className="py-3 px-4 border-b text-gray-700 text-sm">
                          {measurement.client_name || "-"}
                        </td>
                        <td className="py-3 px-4 border-b text-gray-700 text-sm">
                          {measurement.architect_name || "-"}
                        </td>
                        <td className="py-3 px-4 border-b text-gray-700 text-sm">
                          {measurement.manager_name || "-"}
                        </td>
                        <td className="py-3 px-4 border-b text-gray-700 text-sm">
                          {measurement.mobile || "-"}
                        </td>
                        <td className="py-3 px-4 border-b text-sm">
                          {measurement.google_address_link ? (
                            <a
                              href={measurement.google_address_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#4B35CB] font-medium hover:underline"
                            > 
                              {measurement.address || "View Address"}
                            </a>
                          ) : (
                            <span className="text-gray-700">
                              {measurement.address || "-"}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 border-b text-sm">
                          {measurement.google_chat_link ? (
                            <a
                              href={measurement.google_chat_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#4B35CB] font-medium hover:underline"
                            >
                              Open Chat
                            </a>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 border-b text-center">
                          <button
                            onClick={() => handleEdit(measurement)}
                            className="p-2 rounded-full hover:bg-gray-200 transition-colors inline-flex items-center justify-center"
                            title="Edit"
                          >
                            <Edit size={18} className="text-gray-600" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {measurementsData.data.map((measurement, index) => (
                  <div
                    key={measurement.id}
                    className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <span className="inline-block bg-[#4B35CB] text-white text-xs font-semibold px-2 py-1 rounded">
                        #
                        {(currentPage - 1) * measurementsData.per_page +
                          index +
                          1}
                      </span>
                      <button
                        onClick={() => handleEdit(measurement)}
                        className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                        title="Edit"
                      >
                        <Edit size={18} className="text-gray-600" />
                      </button>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          Client Name
                        </span>
                        <p className="text-sm text-gray-800">
                          {measurement.client_name || "-"}
                        </p>
                      </div>

                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          Architect Name
                        </span>
                        <p className="text-sm text-gray-800">
                          {measurement.architect_name || "-"}
                        </p>
                      </div>

                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          Manager Name
                        </span>
                        <p className="text-sm text-gray-800">
                          {measurement.manager_name || "-"}
                        </p>
                      </div>

                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          Mobile
                        </span>
                        <p className="text-sm text-gray-800">
                          {measurement.mobile || "-"}
                        </p>
                      </div>

                      {measurement.google_address_link ? (
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase">
                            Address
                          </span>
                          <a
                            href={measurement.google_address_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#4B35CB] font-medium hover:underline block"
                          >
                            {measurement.address || "View Address"}
                          </a>
                        </div>
                      ) : measurement.address ? (
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase">
                            Address
                          </span>
                          <p className="text-sm text-gray-800">
                            {measurement.address}
                          </p>
                        </div>
                      ) : null}

                      {measurement.google_chat_link && (
                        <div>
                          <a
                            href={measurement.google_chat_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block text-sm text-[#4B35CB] font-medium hover:underline"
                          >
                            Open Google Chat →
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {renderPagination()}
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center min-h-[400px] text-center">
              <div className="text-gray-400 mb-4">
                <Search size={64} />
              </div>
              <p className="text-lg text-gray-600">
                {searchTerm
                  ? "No results found for your search."
                  : "No measurements data available."}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="mt-4 px-4 py-2 bg-[#4B35CB] text-white rounded-lg hover:bg-[#3d2aa3] transition-colors"
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
